import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { uploadInstallerPhoto } from '../../services/photo-upload-service';
import { useInstaller } from '../../contexts/InstallerContext';

export default function PhotoUploader({ isOpen, onClose, jobId, photoType, onUploadComplete }) {
  const installer = useInstaller();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    console.log('📸 Files selected:', files.length);
    setSelectedFiles(files);

    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews(newPreviews);
  };

  const handleRemovePreview = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    URL.revokeObjectURL(previews[index]);
    
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    console.log('🚀 Starting upload...', selectedFiles.length, 'photos');
    setUploading(true);
    setProgress(0);

    try {
      const uploadPromises = selectedFiles.map((file, index) => {
        console.log(`📤 Uploading photo ${index + 1}/${selectedFiles.length}`);
        return uploadInstallerPhoto(jobId, file, photoType, installer).then(() => {
          const newProgress = ((index + 1) / selectedFiles.length) * 100;
          console.log(`✅ Photo ${index + 1} uploaded - ${Math.round(newProgress)}%`);
          setProgress(newProgress);
        });
      });

      await Promise.all(uploadPromises);

      console.log('🎉 All photos uploaded successfully');
      
      previews.forEach(preview => URL.revokeObjectURL(preview));
      
      setSelectedFiles([]);
      setPreviews([]);
      setProgress(0);
      
      if (onUploadComplete) {
        console.log('🔄 Refreshing job data...');
        await onUploadComplete();
      }
      
      console.log('✅ Upload complete, closing modal');
      onClose();
    } catch (error) {
      console.error('❌ Upload failed:', error);
      alert(`Failed to upload photos: ${error.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const photoTypeLabel = photoType === 'before' ? 'Before' : photoType === 'after' ? 'After' : 'Issue';
  const photoTypeColor = photoType === 'before' ? '#3b82f6' : photoType === 'after' ? '#22c55e' : '#f97316';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`📸 Upload ${photoTypeLabel} Photos`}
      size="large"
    >
      <div style={{ padding: '24px' }}>
        {previews.length === 0 ? (
          <label style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            border: `2px dashed ${photoTypeColor}`,
            borderRadius: '16px',
            background: `${photoTypeColor}10`,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            <input
              type="file"
              accept="image/*,image/heic,image/heif"
              multiple
              capture="environment"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📸</div>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: photoTypeColor,
              marginBottom: '8px'
            }}>
              Tap to take or select photos
            </div>
            <div style={{
              fontSize: '13px',
              color: '#666',
              textAlign: 'center',
              padding: '0 20px'
            }}>
              Take photos with your camera or select from gallery
            </div>
          </label>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {previews.map((preview, index) => (
                <div key={index} style={{
                  position: 'relative',
                  paddingBottom: '100%',
                  background: '#f5f2ed',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <button
                    onClick={() => handleRemovePreview(index)}
                    disabled={uploading}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.7)',
                      border: 'none',
                      color: 'white',
                      fontSize: '18px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: uploading ? 0.5 : 1
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <label style={{
              padding: '12px',
              border: `2px dashed ${photoTypeColor}`,
              borderRadius: '12px',
              background: `${photoTypeColor}10`,
              cursor: uploading ? 'not-allowed' : 'pointer',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: '600',
              color: photoTypeColor,
              marginBottom: '20px',
              opacity: uploading ? 0.5 : 1,
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <input
                type="file"
                accept="image/*,image/heic,image/heif"
                multiple
                capture="environment"
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              + Add More Photos
            </label>

            {uploading && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  height: '8px',
                  background: '#e5e5e5',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    background: photoTypeColor,
                    width: `${progress}%`,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#666',
                  textAlign: 'center',
                  marginTop: '8px'
                }}>
                  Uploading... {Math.round(progress)}%
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e5e5',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: '#fafafa'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={uploading}
          style={{ minHeight: '44px' }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploading}
          style={{
            background: photoTypeColor,
            minWidth: '44px',
            minHeight: '44px'
          }}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </Modal>
  );
}